import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeamStore } from '@/stores/teamStore';
import { usePlayerStore } from '@/stores/playerStore';
import { useToast } from '@/hooks/use-toast';
import { useHeaderAction } from '@/contexts/HeaderActionContext';
import { formatDate } from '@/lib/utils';
import type { TeamWithPlayers } from '@/types';

export function TeamsPage() {
  const {
    teams,
    isLoading,
    error,
    loadTeams,
    addTeam,
    updateTeam,
    deleteTeam,
    clearError,
  } = useTeamStore();
  const { players, loadPlayers } = usePlayerStore();
  const { toast } = useToast();
  const { setHeaderAction } = useHeaderAction();

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [player1Id, setPlayer1Id] = useState('');
  const [player2Id, setPlayer2Id] = useState('');
  const [editingTeam, setEditingTeam] = useState<TeamWithPlayers | null>(null);
  const [deletingTeam, setDeletingTeam] = useState<TeamWithPlayers | null>(
    null
  );

  useEffect(() => {
    loadTeams();
    loadPlayers();
  }, [loadTeams, loadPlayers]);

  useEffect(() => {
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error,
      });
      clearError();
    }
  }, [error, toast, clearError]);

  useEffect(() => {
    setHeaderAction(
      <Button
        size="icon"
        onClick={openAddDialog}
        disabled={players.length < 2}
      >
        <Plus className="h-5 w-5" />
      </Button>
    );
    return () => setHeaderAction(null);
  }, [setHeaderAction, players.length]);

  const resetForm = () => {
    setTeamName('');
    setPlayer1Id('');
    setPlayer2Id('');
  };

  const handleAddTeam = async () => {
    if (!teamName.trim() || !player1Id || !player2Id) return;

    const team = await addTeam(teamName.trim(), player1Id, player2Id);
    if (team) {
      toast({
        title: 'Team created',
        description: `${team.name} has been created.`,
      });
      resetForm();
      setIsAddDialogOpen(false);
    }
  };

  const handleUpdateTeam = async () => {
    if (!editingTeam || !teamName.trim() || !player1Id || !player2Id) return;

    const success = await updateTeam(editingTeam.id, {
      name: teamName.trim(),
      player1Id,
      player2Id,
    });
    if (success) {
      toast({
        title: 'Team updated',
        description: `Team has been updated.`,
      });
      setEditingTeam(null);
      resetForm();
      setIsEditDialogOpen(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!deletingTeam) return;

    const success = await deleteTeam(deletingTeam.id);
    if (success) {
      toast({
        title: 'Team deleted',
        description: `${deletingTeam.name} has been removed.`,
      });
      setDeletingTeam(null);
      setIsDeleteDialogOpen(false);
    }
  };

  const openAddDialog = () => {
    resetForm();
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (team: TeamWithPlayers) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setPlayer1Id(team.player1Id);
    setPlayer2Id(team.player2Id);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (team: TeamWithPlayers) => {
    setDeletingTeam(team);
    setIsDeleteDialogOpen(true);
  };

  const getAvailablePlayersForPlayer1 = () => {
    return players.filter((p) => p.id !== player2Id);
  };

  const getAvailablePlayersForPlayer2 = () => {
    return players.filter((p) => p.id !== player1Id);
  };

  const isFormValid = teamName.trim() && player1Id && player2Id;

  if (isLoading) {
    return (
      <div className="container py-6">
        <div className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">Loading teams...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-6 space-y-6">
      {players.length < 2 && (
        <p className="text-sm text-muted-foreground text-center">
          You need at least 2 players to create a team.
        </p>
      )}

      {/* Teams List */}
      {teams.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              No teams yet. Create your first team for 4-player matches!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {teams.map((team) => (
            <Card key={team.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{team.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {team.player1.name} & {team.player2.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Created {formatDate(new Date(team.createdAt))}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(team)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(team)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Team Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Team</DialogTitle>
            <DialogDescription>
              Create a new team by selecting two players.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                placeholder="Enter team name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Player 1</Label>
              <Select value={player1Id} onValueChange={setPlayer1Id}>
                <SelectTrigger>
                  <SelectValue placeholder="Select player 1" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailablePlayersForPlayer1().map((player) => (
                    <SelectItem key={player.id} value={player.id}>
                      {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Player 2</Label>
              <Select value={player2Id} onValueChange={setPlayer2Id}>
                <SelectTrigger>
                  <SelectValue placeholder="Select player 2" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailablePlayersForPlayer2().map((player) => (
                    <SelectItem key={player.id} value={player.id}>
                      {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTeam} disabled={!isFormValid}>
              Create Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
            <DialogDescription>Update the team details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-team-name">Team Name</Label>
              <Input
                id="edit-team-name"
                placeholder="Enter team name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Player 1</Label>
              <Select value={player1Id} onValueChange={setPlayer1Id}>
                <SelectTrigger>
                  <SelectValue placeholder="Select player 1" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailablePlayersForPlayer1().map((player) => (
                    <SelectItem key={player.id} value={player.id}>
                      {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Player 2</Label>
              <Select value={player2Id} onValueChange={setPlayer2Id}>
                <SelectTrigger>
                  <SelectValue placeholder="Select player 2" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailablePlayersForPlayer2().map((player) => (
                    <SelectItem key={player.id} value={player.id}>
                      {player.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleUpdateTeam} disabled={!isFormValid}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Team</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deletingTeam?.name}? This action
              cannot be undone. Historical match statistics for this team will
              be preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteTeam}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
